import { ClockIcon } from '@heroicons/react/24/outline'
import { GlobalConfigType } from 'actions'
import cloneDeep from 'clone-deep'
import { EmailValue, LoanPartiesEmailTo } from 'components/EmailTo'
import { LayoutLoading } from 'components/LayoutLoading'
import { SaveChanges } from 'components/SaveChanges'
import {
  AccountType,
  convertValueToLabel,
  FieldOrigin,
  getInitialLoanFields,
  ILoanOriginSection,
  InputType,
  INVALID_ALL_INPUTS,
  IVisibleProp,
  MultiPropertyFields,
  propertySummaryFields,
} from 'config'
import { isCustomFieldKey } from 'config/loan.input.fields.constants'
import { useIsCompanyEmail } from 'hooks/useIsCompanyEmail'
import { usePermissions } from 'hooks/usePermissions'
import { getDefaultLoanProcessData } from 'pages/LoanStructure/constant'
import {
  ILoan,
  ILoanDetail,
  ILoanDetailChangedJson,
  ILoanProcess,
  IMultipleProperty,
  IPropertyChangedJson,
} from 'pages/LoanStructure/interfaces'
import { LoanDetailInputs } from 'pages/LoanStructure/LoanDetailInputs'
import {
  getLoanDetailChanges,
  getMaximumTotalLoanAmountTip,
  getPropertyDetailChanges,
  getRangeFromString,
  isNonDSCRProduct,
  loanDataConvert,
  loanFieldsValidateAll,
  multiPropertyFieldsValidateAll,
  validateLoanDetailForPricing,
  validateLoanDetailForSaveToPipeline,
  visibleLoansLogic,
  visiblePropertiesLogic,
} from 'pages/LoanStructure/Logic'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { getGeoLocation, getLoanProcessRate, loanRePriceAfterLocked } from 'services'
import { getLoanValidation, updateLoanValidation } from 'services/apis/loan/validation'
import { Button, Checkbox, Modal, TextArea } from 'stories/components'
import {
  canEditLoanStructureDetail,
  confirm,
  InputConvert,
  InputValidate,
  openAuditLog,
  removeComma,
  REQUIRED_FIELD_ERROR_MESSAGE,
} from 'utils'
import { setLoanDefaultData } from 'utils/loan'

export const LoanDetailValidation = () => {
  const { auth, loan, loanDetail, loanFieldSections, fieldProperties, loanFieldProperties } = useSelector(
    (state: any) => {
      return {
        auth: state.auth,
        loan: state.loan as ILoan,
        loanDetail: state.loanDetail as ILoanDetail,
        loanFieldSections: (((state.globalConfigReducer[GlobalConfigType.LoanFields] || {})['sections'] || {})[
          FieldOrigin.LoanStructure
        ] || []) as ILoanOriginSection[],
        fieldProperties: ((state.globalConfigReducer[GlobalConfigType.LoanFields] || {})[FieldOrigin.LoanStructure] ||
          {}) as Record<string, IVisibleProp>,
        loanFieldProperties: state.globalConfigReducer[GlobalConfigType.LoanFields],
      }
    },
  )

  const isCompanyEmail = useIsCompanyEmail()
  const pricingEngineIsOff = !loanDetail?.pricingEngine

  const { hasPermission } = usePermissions()
  const anytimeCanEditLoanDetail = hasPermission('ANYTIME_CAN_EDIT_LOAN_APPLICATION_STRUCTURE')
  const canOverrideLoanDetailValidation = hasPermission('OVERRIDE_LOAN_DETAIL_VALIDATION')
  const canEditLoanDetail = canEditLoanStructureDetail()

  const canPriceAdjust = [AccountType.ADMIN, AccountType.LOCK_DESK].includes(auth.profile.accountType)

  const [visibleFields, setVisibleFields] = useState(['productType', 'transactionType'])
  const [showingFields, setShowingFields] = useState<string[][]>([])
  const [propertyVisibleFields, setPropertyVisibleFields] = useState<string[][]>([])
  const [loanFields, setLoanFields] = useState(getInitialLoanFields())
  const [multiPropertiesFields, setMultiPropertiesFields] = useState<Record<string, InputType>[]>([])
  const [loanProcessData, setLoanProcessData] = useState<ILoanProcess>(getDefaultLoanProcessData())
  const [isLoading, setLoading] = useState(false)
  const [modal, setModal] = useState('')
  const [comment, setComment] = useState('')
  const [notifiers, setNotifiers] = useState<EmailValue>({})

  const [inputLoan, setInputLoan] = useState<ILoan>(setLoanDefaultData({}, loanFieldProperties) as any)
  const [oldInputLoan, setOldInputLoan] = useState<ILoan>()
  const [inputLoanFields, setInputLoanFields] = useState(getInitialLoanFields())
  const [inputMultiPropertiesFields, setInputMultiPropertiesFields] = useState<Record<string, InputType>[]>([])
  const [inputVisibleFields, setInputVisibleFields] = useState(['productType', 'transactionType'])
  const [inputShowingFields, setInputShowingFields] = useState<string[][]>([])
  const [inputPropertyVisibleFields, setInputPropertyVisibleFields] = useState<string[][]>([])
  const [changedLoanDetailJson, setChangedLoanDetailJson] = useState<Record<string, ILoanDetailChangedJson>>({})
  const [changedPropertyDetailJson, setChangedPropertyDetailJson] = useState<IPropertyChangedJson[]>([])
  const [selectedLoanChanges, setSelectedLoanChanges] = useState<string[]>([])
  const [selectedPropertyChanges, setSelectedPropertyChanges] = useState<{ index: number; key: string }[]>([])
  const [isChanged, setChanged] = useState(false)

  const brokerCanNotEditDetail = useMemo(() => {
    let rlt = false
    if (loanProcessData.rateLocked) rlt = true
    if (loanDetail.restructureApplicationRelease === false) rlt = true
    return rlt
  }, [loanProcessData.rateLocked, loanDetail.restructureApplicationRelease])

  const isMultipleProperties = useMemo(() => {
    return !!loan.isMultipleProperties || loanFields.isMultipleProperties.value
  }, [loan, loanFields])

  const isInputMultipleProperties = useMemo(() => {
    return !!inputLoan.isMultipleProperties || inputLoanFields.isMultipleProperties.value
  }, [inputLoan, inputLoanFields])

  const maximumTotalLoanAmountTip = useMemo(() => {
    return getMaximumTotalLoanAmountTip(loan, {})
  }, [loan])

  const removedProperties = useMemo(() => {
    if (!loan?.multipleProperties || !inputLoan?.multipleProperties) return []
    return loan.multipleProperties
      .filter((item) => !inputLoan.multipleProperties.find((v) => v.id === item.id))
      .map((item) => item.id)
  }, [loan, inputLoan])

  useEffect(() => {
    const tempVisibleFields = cloneDeep(visibleLoansLogic())
    setVisibleFields(tempVisibleFields)

    setLoanFields(loanFieldsValidateAll(loanFields, loanDetail.loanGlobalSettings, fieldProperties, false))
    loanDataConvert(loanFields)

    const nMultiPropertiesFields: Record<string, InputType>[] = []
    const nPropertyVisibleFields: string[][] = []
    loan?.multipleProperties?.forEach((_, index) => {
      const nInputs = cloneDeep(getInitialLoanFields())
      nMultiPropertiesFields.push(multiPropertyFieldsValidateAll(index, nInputs, fieldProperties))
      nPropertyVisibleFields.push(visiblePropertiesLogic(index))
    })
    setMultiPropertiesFields(nMultiPropertiesFields)
    setPropertyVisibleFields(nPropertyVisibleFields)
    setChanged(false)

    setDefaultNotifiers()

    setLoading(true)
    getLoanProcessRate()
      .then((res) => {
        if (res.success) setLoanProcessData(res.data)
      })
      .finally(() => fetchLoanValidation())
  }, [])

  useEffect(() => {
    const nShowedFields: string[][] = []
    loanFieldSections.map((section) => {
      let newFields = section.fields.filter((key) => visibleFields.includes(key))
      if (loan.isMultipleProperties)
        newFields = newFields.filter((key) => !MultiPropertyFields.includes(key) || key === 'subjectPropertyAddress')
      if (newFields.length > 0) nShowedFields.push(newFields)
    })
    if (loan.isMultipleProperties) {
      const propertyFields = MultiPropertyFields.filter(
        (key) => key === 'subjectPropertyAddress' || visibleFields.includes(key),
      )
      nShowedFields.forEach((section) => {
        const addressIndex = section.findIndex((key) => key === 'subjectPropertyAddress')
        if (addressIndex !== -1) section = section.splice(addressIndex, 1, ...propertyFields)
      })
      const propertySectionIndex = nShowedFields.findIndex((section) => section.includes('subjectPropertyAddress'))
      nShowedFields.splice(propertySectionIndex, 0, propertySummaryFields)
    }
    setShowingFields(nShowedFields)
  }, [visibleFields, loan.isMultipleProperties])

  useEffect(() => {
    const nShowedFields: string[][] = []
    loanFieldSections.map((section) => {
      let newFields = section.fields.filter((key) => inputVisibleFields.includes(key))
      if (inputLoan.isMultipleProperties)
        newFields = newFields.filter((key) => !MultiPropertyFields.includes(key) || key === 'subjectPropertyAddress')
      if (newFields.length > 0) nShowedFields.push(newFields)
    })
    if (inputLoan.isMultipleProperties) {
      const propertyFields = MultiPropertyFields.filter(
        (key) => key === 'subjectPropertyAddress' || inputVisibleFields.includes(key),
      )
      nShowedFields.forEach((section) => {
        const addressIndex = section.findIndex((key) => key === 'subjectPropertyAddress')
        if (addressIndex !== -1) section = section.splice(addressIndex, 1, ...propertyFields)
      })
      const propertySectionIndex = nShowedFields.findIndex((section) => section.includes('subjectPropertyAddress'))
      nShowedFields.splice(propertySectionIndex, 0, propertySummaryFields)
    }
    setInputShowingFields(nShowedFields)
  }, [inputVisibleFields, inputLoan.isMultipleProperties])

  const setDefaultNotifiers = () => {
    const defaultNotifiers: EmailValue = {}
    if (loanDetail?.parties.loanOfficer?.email) defaultNotifiers[loanDetail?.parties.loanOfficer?.email] = 'to'
    if (loanDetail?.parties.loanProcessor?.email) defaultNotifiers[loanDetail?.parties.loanProcessor?.email] = 'to'
    if (loanDetail?.parties.accountExecutive?.email)
      defaultNotifiers[loanDetail?.parties.accountExecutive?.email] = 'to'
    setNotifiers(defaultNotifiers)
  }

  const fetchLoanValidation = () => {
    setLoading(true)
    getLoanValidation()
      .then((res) => {
        if (!!res) {
          setInputLoan(res)
          setOldInputLoan(res)
          setChangedLoanDetailJson(res.changedLoanDetailJson)
          setChangedPropertyDetailJson(res.changedPropertyDetailJson)
          setSelectedLoanChanges(res.selectedLoanChanges)
          setSelectedPropertyChanges(res.selectedPropertyChanges)
        } else {
          const newLoan = cloneDeep(loan)
          Object.keys(newLoan).forEach((key) => {
            if (key === 'multipleProperties') {
              newLoan[key].forEach((item) => {
                Object.keys(item).forEach((key1) => {
                  ;(item as any)[key1] = ''
                })
              })
            } else (newLoan as any)[key] = ''
          })
          setInputLoan(newLoan)
          setOldInputLoan(undefined)
        }

        const tempVisibleFields = cloneDeep(visibleLoansLogic(res))
        setInputVisibleFields(tempVisibleFields)

        const nMultiPropertiesFields: Record<string, InputType>[] = []
        const nPropertyVisibleFields: string[][] = []
        const multipleProperties = res?.multipleProperties || loan?.multipleProperties || []
        multipleProperties.forEach((_: any, index: number) => {
          const nInputs = cloneDeep(getInitialLoanFields())
          nMultiPropertiesFields.push(multiPropertyFieldsValidateAll(index, nInputs, fieldProperties, res))
          nPropertyVisibleFields.push(visiblePropertiesLogic(index, res))
        })
        setInputMultiPropertiesFields(nMultiPropertiesFields)
        setInputPropertyVisibleFields(nPropertyVisibleFields)

        const newInputLoanFields = cloneDeep(getInitialLoanFields())
        newInputLoanFields.subjectPropertyAddress.needCountyUpdate = false
        setInputLoanFields(
          loanFieldsValidateAll(newInputLoanFields, loanDetail.loanGlobalSettings, fieldProperties, false, res),
        )
      })
      .finally(() => setLoading(false))
  }

  const onValidate = () => {
    setChangedLoanDetailJson(
      getLoanDetailChanges(
        loan,
        inputLoan,
        inputLoanFields,
        loanProcessData,
        loanProcessData,
        inputMultiPropertiesFields,
        inputVisibleFields,
        inputPropertyVisibleFields,
      ),
    )
    setChangedPropertyDetailJson(
      getPropertyDetailChanges(
        loan,
        inputLoan,
        inputMultiPropertiesFields,
        inputPropertyVisibleFields,
        loanProcessData,
        loanProcessData,
        isInputMultipleProperties,
      ),
    )
    setChanged(true)
  }

  const onChangeLoanInputFields = async (key: string, e: any) => {
    const value = InputConvert(inputLoanFields[key], e)
    const newInputLoan = cloneDeep(inputLoan)
    ;(newInputLoan as any)[key] = value
    const nPropertyVisibleFields: string[][] = []
    inputMultiPropertiesFields.forEach((_, index) =>
      nPropertyVisibleFields.push(visiblePropertiesLogic(index, newInputLoan)),
    )
    setInputVisibleFields(visibleLoansLogic(newInputLoan))
    setInputPropertyVisibleFields(nPropertyVisibleFields)
    const error = InputValidate({ ...inputLoanFields[key], value }, loanDetail, newInputLoan)
    let tempLoanFields = cloneDeep(inputLoanFields)

    if (!!inputLoanFields[key] && error !== inputLoanFields[key].error) tempLoanFields[key].error = error

    if (key === 'subjectPropertyAddress' && !!value) newInputLoan['subjectPropertyAddressTBD'] = false
    if (key === 'subjectPropertyAddressTBD') {
      newInputLoan['subjectPropertyAddress'] = !!value ? 'To Be Determined' : ''
      tempLoanFields['subjectPropertyAddress'].error = !!value ? '' : REQUIRED_FIELD_ERROR_MESSAGE
    }
    if (key === 'transactionType' && value === 'Refinance') {
      ;(newInputLoan as any)['propertyPurchasePrice'] = null
      if (newInputLoan.isMultipleProperties) {
        const { multipleProperties } = newInputLoan
        multipleProperties.forEach((item) => {
          item.propertyPurchasePrice = null as any
        })
        newInputLoan['multipleProperties'] = multipleProperties
      }
    }
    if (key === 'experience') {
      const { from, to } = getRangeFromString(value)
      if (from === to && from !== null) newInputLoan['howManyExperiences'] = from
    }
    if (key === 'rehabBudget') {
      const { minTotalConstructionFinanced } = loanDetail.loanGlobalSettings
      if (minTotalConstructionFinanced === 100) {
        newInputLoan['constructionReserve'] = removeComma(value)
      }
    }
    if (key === 'productType' && isNonDSCRProduct(value)) newInputLoan['amortizationType'] = 'IO - Fixed'
    if (key === 'propertyType' && ['SFR-Detached', 'SFR-Attached', 'Industrial'].includes(value)) {
      newInputLoan['numberOfUnits'] = 1
      tempLoanFields['numberOfUnits'].readOnly = true
      tempLoanFields['numberOfUnits'].type = 'text'
    }
    tempLoanFields = loanFieldsValidateAll(
      cloneDeep(tempLoanFields),
      loanDetail.loanGlobalSettings,
      fieldProperties,
      false,
      newInputLoan,
    )
    setInputLoanFields(tempLoanFields)
    setInputLoan(newInputLoan)
    setChangedLoanDetailJson({})
    setChangedPropertyDetailJson([])
    setChanged(false)
  }

  const onChangePropertyFields = async (index: number, key: string, e: any) => {
    const value = InputConvert(inputMultiPropertiesFields[index][key], e)
    const nMultiProperties = cloneDeep(inputLoan?.multipleProperties || [])
    ;(nMultiProperties[index] as any)[key] = value
    const error = InputValidate({ ...inputMultiPropertiesFields[index][key], value }, loanDetail, inputLoan)
    let tempLoanFields = cloneDeep(inputMultiPropertiesFields)
    let needLoanFieldChange = false
    if (!!inputMultiPropertiesFields[index][key] && error !== inputMultiPropertiesFields[index][key].error) {
      tempLoanFields[index][key].error = error
      needLoanFieldChange = true
    }
    if (key === 'subjectPropertyAddress' && !!value) nMultiProperties[index].subjectPropertyAddressTBD = false
    if (key === 'subjectPropertyAddressTBD') {
      needLoanFieldChange = true
      nMultiProperties[index].subjectPropertyAddress = !!value ? 'To Be Determined' : ''
      tempLoanFields[index]['subjectPropertyAddress'].error = !!value ? '' : REQUIRED_FIELD_ERROR_MESSAGE
    }
    if (key === 'propertyType' && ['SFR-Detached', 'SFR-Attached', 'Industrial'].includes(value)) {
      needLoanFieldChange = true
      nMultiProperties[index].numberOfUnits = 1
      tempLoanFields[index]['numberOfUnits'].readOnly = true
      tempLoanFields[index]['numberOfUnits'].type = 'text'
    }
    if (key === 'rehabBudget') {
      const { minTotalConstructionFinanced } = loanDetail.loanGlobalSettings
      if (minTotalConstructionFinanced === 100) {
        needLoanFieldChange = true
        nMultiProperties[index].constructionReserve = removeComma(value)
      }
    }

    const loanData = cloneDeep(inputLoan)
    loanData.multipleProperties = nMultiProperties
    setInputLoan(loanData)

    if (needLoanFieldChange) setInputMultiPropertiesFields(tempLoanFields)
    const nPropertyVisibleFields = cloneDeep(inputPropertyVisibleFields)
    nPropertyVisibleFields[index] = visiblePropertiesLogic(index, loanData)
    setInputPropertyVisibleFields(nPropertyVisibleFields)

    const nMultiPropertiesFields: Record<string, InputType>[] = []
    loanData?.multipleProperties?.forEach((_, index) => {
      const nInputs = cloneDeep(getInitialLoanFields())
      nMultiPropertiesFields.push(multiPropertyFieldsValidateAll(index, nInputs, fieldProperties, loanData))
    })
    setInputMultiPropertiesFields(nMultiPropertiesFields)
    setChangedLoanDetailJson({})
    setChangedPropertyDetailJson([])
    setChanged(false)
  }

  const onAddProperty = () => {
    const newInputLoan = cloneDeep(inputLoan)
    const nMultiProperties = cloneDeep(inputLoan?.multipleProperties || [])
    const newProperty: any = {
      id: Date.now(),
      subjectPropertyAddress: null,
      proposedOccupancy: null,
      propertyHasBeenListed: null,
      asIsValue: null,
      secondLien: null,
      secondLienPayment: null,
      lienPayoff: null,
      propertyType: null,
      condoType: null,
      numberOfUnits: null,
      proposedMonthlyRent: null,
      proposedMonthlyTaxes: null,
      proposedMonthlyInsurance: null,
      proposedMonthlyHoaDues: null,
      floodInsurance: null,
      schoolTax: null,
      otherTax: null,
    }
    nMultiProperties.push(newProperty)
    newInputLoan.multipleProperties = nMultiProperties

    const nMultiPropertiesFields: Record<string, InputType>[] = []
    const nPropertyVisibleFields: string[][] = []
    newInputLoan?.multipleProperties?.forEach((_, index) => {
      const nInputs = cloneDeep(getInitialLoanFields())
      nMultiPropertiesFields.push(multiPropertyFieldsValidateAll(index, nInputs, fieldProperties, newInputLoan))
      nPropertyVisibleFields.push(visiblePropertiesLogic(index, newInputLoan))
    })
    setInputLoan(newInputLoan)
    setInputMultiPropertiesFields(nMultiPropertiesFields)
    setInputPropertyVisibleFields(nPropertyVisibleFields)
    setChangedLoanDetailJson({})
    setChangedPropertyDetailJson([])
    setChanged(false)
  }

  const onRemoveProperty = async (index: number) => {
    const newInputLoan = cloneDeep(inputLoan)
    const nMultiProperties = cloneDeep(inputLoan?.multipleProperties || [])
    const content = (
      <div className="text-gray-400 mb-4 text-[18px]">
        Do you want to remove this property?
        <br />
        <span className="text-gray-600 text-base">
          Property Address: {nMultiProperties[index].subjectPropertyAddress || 'N/A'}
        </span>
      </div>
    )
    const result = await confirm(content)
    if (!result) return
    nMultiProperties.splice(index, 1)
    newInputLoan.multipleProperties = nMultiProperties

    const nMultiPropertiesFields: Record<string, InputType>[] = []
    const nPropertyVisibleFields: string[][] = []
    newInputLoan?.multipleProperties?.forEach((_, index) => {
      const nInputs = cloneDeep(getInitialLoanFields())
      nMultiPropertiesFields.push(multiPropertyFieldsValidateAll(index, nInputs, fieldProperties, newInputLoan))
      nPropertyVisibleFields.push(visiblePropertiesLogic(index, newInputLoan))
    })
    setInputLoan(newInputLoan)
    setInputMultiPropertiesFields(nMultiPropertiesFields)
    setInputPropertyVisibleFields(nPropertyVisibleFields)
    setChangedLoanDetailJson({})
    setChangedPropertyDetailJson([])
    setChanged(false)
  }

  const onSelectLoanChange = (key: string) => {
    setSelectedLoanChanges((prev) => (prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]))
    setChanged(true)
  }

  const onSelectPropertyChange = (index: number, key: string) => {
    setSelectedPropertyChanges((prev) => {
      const exists = prev.some((item) => item.index === index && item.key === key)
      return exists ? prev.filter((item) => !(item.index === index && item.key === key)) : [...prev, { index, key }]
    })
    setChanged(true)
  }

  const onConfirmOverride = () => {
    const nMultiPropertiesFields: Record<string, InputType>[] = []
    inputLoan?.multipleProperties?.forEach((_, index) => {
      const nInputs = cloneDeep(getInitialLoanFields())
      nMultiPropertiesFields.push(multiPropertyFieldsValidateAll(index, nInputs, fieldProperties, inputLoan))
    })
    setInputMultiPropertiesFields(nMultiPropertiesFields)
    const nInputLoanFields = loanFieldsValidateAll(
      inputLoanFields,
      loanDetail.loanGlobalSettings,
      fieldProperties,
      false,
      inputLoan,
    )
    setInputLoanFields(nInputLoanFields)

    const canOverride = validateLoanDetailForPricing(
      inputLoan,
      nInputLoanFields,
      nMultiPropertiesFields,
      loanDetail,
      {},
      inputVisibleFields,
      inputPropertyVisibleFields,
      isInputMultipleProperties,
    )
    if (!canOverride) return toast(INVALID_ALL_INPUTS, { type: 'error' })

    const newNotifiers: any = cloneDeep(notifiers)
    if (isCompanyEmail) newNotifiers.from = 'self'
    else newNotifiers.from = 'HOME_EMAIL'
    setNotifiers(newNotifiers)
    setModal('override')
  }

  const onOverride = async () => {
    setLoading(true)
    let json: any = {}
    const fields = getInitialLoanFields()
    const finalLoanChanges: Record<string, ILoanDetailChangedJson> = {}
    const finalPropertyChanges: IPropertyChangedJson[] = []
    Object.keys(changedLoanDetailJson).forEach((key) => {
      if (selectedLoanChanges.includes(key)) finalLoanChanges[key] = changedLoanDetailJson[key]
    })
    changedPropertyDetailJson.forEach((item) => {
      if (selectedPropertyChanges.find((change) => change.index === item.index && change.key === item.key))
        finalPropertyChanges.push(item)
    })
    Object.keys(finalLoanChanges).forEach((key) => {
      if (fields[key] || isCustomFieldKey(key)) json[key] = finalLoanChanges[key].changed
    })
    if (!!finalPropertyChanges.length || !!removedProperties.length) {
      let { multipleProperties } = loan
      finalPropertyChanges.forEach((item) => {
        if (fields[item.key]) {
          const targetIndex = multipleProperties.findIndex((v) => v.id === item.id)
          if (targetIndex !== -1) {
            ;(multipleProperties[targetIndex] as any)[item.key] = item.changed
          } else {
            const emptyProperty: IMultipleProperty = {
              id: item.id,
              subjectPropertyAddress: '',
              subjectPropertyAddressTBD: false,
              proposedOccupancy: '',
              propertyHasBeenListed: undefined as any,
              propertyPurchasePrice: NaN,
              asIsValue: NaN,
              afterRepairValue: NaN,
              rehabBudget: NaN,
              constructionReserve: NaN,
              secondLien: NaN,
              secondLienPayment: NaN,
              lienPayoff: NaN,
              propertyType: '',
              condoType: '',
              numberOfUnits: NaN,
              proposedMonthlyRent: NaN,
              proposedMonthlyTaxes: NaN,
              proposedMonthlyInsurance: NaN,
              proposedMonthlyHoaDues: NaN,
              floodInsurance: NaN,
              schoolTax: NaN,
              otherTax: NaN,
            }
            multipleProperties.push({
              ...emptyProperty,
              [item.key]: item.changed,
            })
          }
        }
      })
      multipleProperties = multipleProperties.filter((item) => !removedProperties.includes(item.id))
      json['multipleProperties'] = multipleProperties
    }

    json.changedJson = finalLoanChanges
    json.changedPropertyJson = finalPropertyChanges
    json.removedProperties = removedProperties
    json.rePriceJson = {
      originLoanProcessData: loanProcessData,
      loanProcessData: { rate: 0 },
      showingFields: inputShowingFields,
      pricingEngineIsOff: pricingEngineIsOff,
      noPriceReduction: false,
      ratesheetData: {},
      overridePricing: false,
      pendingTaskID: null,
      revalidate: false,
      exceptionReviewRequest: false,
      lockExpired: false,
      overrideLoanDetail: true,
    }

    json.emailTo = notifiers
    json.comment = comment
    json.isNonDscrProduct = isNonDSCRProduct(loan.productType)

    const res = await loanRePriceAfterLocked(json)
    if (res.success) {
      if (Object.keys(json.changedJson).includes('subjectPropertyAddress')) {
        const geoLocation = await getGeoLocation()
        const newInputLoan = cloneDeep(inputLoan)
        newInputLoan.geoLocation = geoLocation
        setInputLoan(newInputLoan)
      }
    }
    setLoading(false)
    setComment('')
  }

  const onSave = () => {
    const nMultiPropertiesFields: Record<string, InputType>[] = []
    inputLoan?.multipleProperties?.forEach((_, index) => {
      const nInputs = cloneDeep(getInitialLoanFields())
      nMultiPropertiesFields.push(multiPropertyFieldsValidateAll(index, nInputs, fieldProperties, inputLoan))
    })
    setInputMultiPropertiesFields(nMultiPropertiesFields)
    const nInputLoanFields = loanFieldsValidateAll(
      inputLoanFields,
      loanDetail.loanGlobalSettings,
      fieldProperties,
      false,
      inputLoan,
    )
    setInputLoanFields(nInputLoanFields)

    const canSave = validateLoanDetailForSaveToPipeline(
      inputLoan,
      nInputLoanFields,
      nMultiPropertiesFields,
      inputVisibleFields,
      inputPropertyVisibleFields,
      isInputMultipleProperties,
    )
    if (!canSave) return toast(INVALID_ALL_INPUTS, { type: 'error' })

    let reqBody: Record<string, any> = {
      multipleProperties: inputLoan?.multipleProperties || [],
      changedLoanDetailJson,
      changedPropertyDetailJson,
      selectedLoanChanges,
      selectedPropertyChanges,
    }
    if (!oldInputLoan)
      inputVisibleFields.map((field) => {
        reqBody[field] = (inputLoan as any)[field]
      })
    else
      inputVisibleFields.map((field) => {
        if ((inputLoan as any)[field] != (oldInputLoan as any)[field]) reqBody[field] = (inputLoan as any)[field]
      })

    setLoading(true)
    updateLoanValidation(reqBody)
      .then(() => setChanged(false))
      .finally(() => setLoading(false))
  }

  const showHistory = async (type: 'selection' | 'propertySelection', key: string, index: number) => {
    const options = {
      table: 'Loan Validation',
      field: loanFields[key].title,
      fieldLabel: loanFields[key].title,
      keys: {
        type,
        index,
        field: key,
        loanNumber: loanDetail.loanNumber,
      },
    }
    openAuditLog(options)
  }

  const renderLoanDetailChanges = useMemo(() => {
    const loanKeys = Object.keys(changedLoanDetailJson)
    if (loanKeys.length + changedPropertyDetailJson.length === 0) return null

    return (
      <div className="mb-4">
        <div className="overflow-auto">
          <table className="table cursor-pointer shadow w-full text-sm">
            <thead className="text-gray-700 bg-gray-50">
              <tr className="">
                <th scope="col" className="border p-1">
                  Field
                </th>
                <th scope="col" className="border p-1">
                  Current
                </th>
                <th scope="col" className="border p-1">
                  New
                </th>
                <th scope="col" className="border p-1">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {loanKeys.map((key, index) => {
                const item = changedLoanDetailJson[key]
                const origin = convertValueToLabel(key, item.origin, fieldProperties)
                const changed = convertValueToLabel(key, item.changed, fieldProperties)
                const selected = selectedLoanChanges.includes(key)

                return (
                  <tr key={index}>
                    <td className="border px-3 py-1">{item.label}</td>
                    <td className="border px-3 py-1">{origin}</td>
                    <td className={`border px-3 py-1 ${selected ? 'bg-green-300' : 'bg-red-300'}`}>{changed}</td>
                    <td className="border px-3 py-1">
                      <div className="flex justify-center items-center gap-1">
                        <Checkbox
                          id={`approve`}
                          title=""
                          size={3}
                          value={selected}
                          color="gray"
                          className="whitespace-nowrap text-left w-fit"
                          onClick={() => onSelectLoanChange(key)}
                        />

                        <ClockIcon
                          className="h-[14px] w-[14px] text-gray-600 cursor-pointer"
                          onClick={() => showHistory('selection', key, -1)}
                        />
                      </div>
                    </td>
                  </tr>
                )
              })}

              {changedPropertyDetailJson
                .sort((x, y) => x.index - y.index)
                .map((item, index) => {
                  const origin = convertValueToLabel(item.key, item.origin, fieldProperties)
                  const changed = convertValueToLabel(item.key, item.changed, fieldProperties)
                  const selected = !!selectedPropertyChanges.find(
                    (change) => change.index === item.index && change.key === item.key,
                  )

                  return (
                    <tr key={index}>
                      <td className="border px-3 py-1">
                        {item.label} (Property {item.index + 2})
                      </td>
                      <td className="border px-3 py-1">{origin}</td>
                      <td className={`border px-3 py-1 ${selected ? 'bg-green-300' : 'bg-red-300'}`}>{changed}</td>
                      <td className="border px-3 py-1">
                        <div className="flex justify-center items-center gap-1">
                          <Checkbox
                            id={`approve`}
                            title=""
                            size={3}
                            value={selected}
                            color="gray"
                            className="whitespace-nowrap text-left w-fit"
                            onClick={() => onSelectPropertyChange(item.index, item.key)}
                          />

                          <ClockIcon
                            className="h-[14px] w-[14px] text-gray-600 cursor-pointer"
                            onClick={() => showHistory('propertySelection', item.key, item.index)}
                          />
                        </div>
                      </td>
                    </tr>
                  )
                })}
            </tbody>
          </table>
        </div>
      </div>
    )
  }, [changedLoanDetailJson, changedPropertyDetailJson, selectedLoanChanges, selectedPropertyChanges])

  const renderSelectedLoanDetailChanges = useMemo(() => {
    const finalLoanChanges: Record<string, ILoanDetailChangedJson> = {}
    const finalPropertyChanges: IPropertyChangedJson[] = []
    Object.keys(changedLoanDetailJson).forEach((key) => {
      if (selectedLoanChanges.includes(key)) finalLoanChanges[key] = changedLoanDetailJson[key]
    })
    changedPropertyDetailJson.forEach((item) => {
      if (selectedPropertyChanges.find((change) => change.index === item.index && change.key === item.key))
        finalPropertyChanges.push(item)
    })

    const loanKeys = Object.keys(finalLoanChanges)
    if (loanKeys.length + finalPropertyChanges.length === 0) return null

    return (
      <div className="mb-4">
        <div className="overflow-auto">
          <table className="table cursor-pointer shadow w-full text-sm">
            <thead className="text-gray-700 bg-gray-50">
              <tr className="">
                <th scope="col" className="border p-1">
                  Field
                </th>
                <th scope="col" className="border p-1">
                  Current
                </th>
                <th scope="col" className="border p-1">
                  New
                </th>
              </tr>
            </thead>
            <tbody>
              {loanKeys.map((key, index) => {
                const item = finalLoanChanges[key]
                const origin = convertValueToLabel(key, item.origin, fieldProperties)
                const changed = convertValueToLabel(key, item.changed, fieldProperties)

                return (
                  <tr key={index}>
                    <td className="border px-3 py-1">{item.label}</td>
                    <td className="border px-3 py-1">{origin}</td>
                    <td className={`border px-3 py-1 bg-red-300`}>{changed}</td>
                  </tr>
                )
              })}

              {finalPropertyChanges
                .sort((x, y) => x.index - y.index)
                .map((item, index) => {
                  const origin = convertValueToLabel(item.key, item.origin, fieldProperties)
                  const changed = convertValueToLabel(item.key, item.changed, fieldProperties)

                  return (
                    <tr key={index}>
                      <td className="border px-3 py-1">
                        {item.label} (Property {item.index + 2})
                      </td>
                      <td className="border px-3 py-1">{origin}</td>
                      <td className={`border px-3 py-1 bg-red-300`}>{changed}</td>
                    </tr>
                  )
                })}
            </tbody>
          </table>
        </div>
      </div>
    )
  }, [changedLoanDetailJson, changedPropertyDetailJson, selectedLoanChanges, selectedPropertyChanges])

  return (
    <div className="relative">
      <LayoutLoading show={isLoading} />
      <SaveChanges show={isChanged} label="Save Validation" onSave={onSave} />

      <div className="grid grid-cols-2 gap-6 md:gap-2">
        <div className="col-span-1">
          <LoanDetailInputs
            loan={loan}
            loanDetail={loanDetail}
            loanFields={loanFields}
            multiPropertiesFields={multiPropertiesFields}
            loan_number={loanDetail.loanNumber}
            loanProcessData={loanProcessData}
            showingFields={showingFields}
            visibleFields={visibleFields}
            propertyVisibleFields={propertyVisibleFields}
            isMultipleProperties={isMultipleProperties}
            isAdmin={false}
            canEditLoanDetail={false}
            canPriceAdjust={false}
            exceptionApproved={true}
            anytimeCanEditLoanDetail={false}
            changedLoanDetailJson={{}}
            changedPropertyDetailJson={[]}
            brokerCanNotEditDetail={true}
            originLoanDetail={loan}
            priceAdjustmentObject={{}}
            sandBoxMode={false}
            isPool={true}
            showMap={false}
            maximumTotalLoanAmountTip={maximumTotalLoanAmountTip}
            onBlur={() => {}}
            onChangeLoanInputFields={() => {}}
            onAddProperty={() => {}}
            onChangePropertyFields={() => {}}
            onBlurProperty={() => {}}
            onRemoveProperty={() => {}}
          />
        </div>
        <div className="col-span-1">
          <LoanDetailInputs
            loan={inputLoan}
            loanDetail={loanDetail}
            loanFields={inputLoanFields}
            multiPropertiesFields={inputMultiPropertiesFields}
            loan_number={loanDetail.loanNumber}
            loanProcessData={loanProcessData}
            showingFields={inputShowingFields}
            visibleFields={inputVisibleFields}
            propertyVisibleFields={inputPropertyVisibleFields}
            isMultipleProperties={isInputMultipleProperties}
            isAdmin={false}
            canEditLoanDetail={canEditLoanDetail}
            canPriceAdjust={canPriceAdjust}
            exceptionApproved={true}
            anytimeCanEditLoanDetail={anytimeCanEditLoanDetail}
            changedLoanDetailJson={changedLoanDetailJson}
            changedPropertyDetailJson={changedPropertyDetailJson}
            brokerCanNotEditDetail={brokerCanNotEditDetail}
            originLoanDetail={loan}
            priceAdjustmentObject={{}}
            sandBoxMode={false}
            isPool={false}
            showMap={false}
            logTable="Loan Validation"
            maximumTotalLoanAmountTip={maximumTotalLoanAmountTip}
            onBlur={() => {}}
            onChangeLoanInputFields={onChangeLoanInputFields}
            onAddProperty={onAddProperty}
            onChangePropertyFields={onChangePropertyFields}
            onBlurProperty={() => {}}
            onRemoveProperty={onRemoveProperty}
          />
        </div>
      </div>

      {renderLoanDetailChanges}

      <div className="flex justify-center gap-4">
        <Button onClick={onValidate}>Validate</Button>
        <Button
          onClick={onConfirmOverride}
          disabled={!canOverrideLoanDetailValidation || !renderSelectedLoanDetailChanges}
        >
          Override Loan Structure
        </Button>
      </div>

      {modal === 'override' && (
        <Modal
          isOpen
          title={`Are you sure want to override the loan detail?`}
          titleOkay="Yes, I'm sure"
          titleCancel="No, cancel"
          onClose={() => {
            setModal('')
            setComment('')
          }}
          onOk={() => {
            setModal('')
            onOverride()
          }}
        >
          <div className="confirmModalLoanChange">
            <div className="text-left text-[13px]">{renderSelectedLoanDetailChanges}</div>

            {canEditLoanDetail && (
              <div className="mt-4 text-[13px]">
                <div className="border-b mb-2 font-bold">Send Notification To</div>
                <LoanPartiesEmailTo
                  loanNumber={Number(loanDetail.loanNumber)}
                  value={notifiers}
                  data={[]}
                  onChange={setNotifiers}
                  loadParties
                />

                <TextArea title="Comment" rows={3} onChange={(v) => setComment(v)} className="mt-4" value={comment} />
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  )
}
